import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';

import { classlist } from '~/_shared/design-system/lib/classlist';
import { IconButton } from '~/_shared/design-system/ui/atoms/button/icon/icon-button';

import './drawer.css';

interface DrawerRootProps {
	children: ReactNode;
	default_open?: boolean;
	open?: boolean;
	on_open_change?: (open: boolean) => void;
}

const DrawerRoot = ({
	children,
	default_open,
	open,
	on_open_change,
}: DrawerRootProps) => (
	<Dialog.Root
		defaultOpen={default_open}
		open={open}
		onOpenChange={on_open_change}
	>
		{children}
	</Dialog.Root>
);

interface DrawerTriggerProps {
	children: ReactNode;
	id?: string;
	'aria-label': string;
}

const DrawerTrigger = ({
	children,
	id = 'DrawerTrigger',
	'aria-label': aria_label,
}: DrawerTriggerProps) => (
	<Dialog.Trigger asChild>
		<IconButton id={id} aria-label={aria_label}>
			{children}
		</IconButton>
	</Dialog.Trigger>
);

interface DrawerBodyProps {
	children: ReactNode;
	'aria-label'?: string;
}

const DrawerBody = ({
	children,
	'aria-label': aria_label = 'Main menu',
}: DrawerBodyProps) => (
	<Dialog.Portal>
		<Dialog.Overlay className='drawer__overlay' />
		<Dialog.Content className='drawer__panel' aria-label={aria_label}>
			<Dialog.Title className='sr-only'>{aria_label}</Dialog.Title>
			<Dialog.Description className='sr-only'>
				Navigation menu and account actions.
			</Dialog.Description>
			{children}
		</Dialog.Content>
	</Dialog.Portal>
);

interface DrawerSectionProps {
	id?: string;
	className?: string;
	children: ReactNode;
}

const DrawerSection = ({ id, className, children }: DrawerSectionProps) => (
	<section id={id} className={classlist('drawer__section', className)}>
		{children}
	</section>
);

const Drawer = Object.assign(DrawerRoot, {
	Trigger: DrawerTrigger,
	Body: DrawerBody,
	Section: DrawerSection,
});

export type {
	DrawerBodyProps,
	DrawerRootProps,
	DrawerSectionProps,
	DrawerTriggerProps,
};
export { Drawer };
